import {
  JsonHubProtocol,
  HttpTransportType,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import Logger from 'utils/logger';
import { getAccessToken } from 'common/api';
import {
  HOME_FETCH_CASES_SUCCESS,
  HOME_FETCH_CASES_SUCCESS_DUMMY,
} from 'features/home/redux/constants';
import {
  CASE_FETCH_CASE_DETAILS_FAILURE,
  CASE_FETCH_CASE_DETAILS_SUCCESS,
  CASE_REALTIME_TRANSCRIPT_MESSAGE_RECEIVED,
} from 'features/case/redux/constants';
import { AUTH_SET_HUB_CONNECTION_ID_FAILURE } from 'features/auth/redux/constants';
import {
  selectCurrentCaseId,
  selectMatchedUrl,
  selectQuery,
  selectTheatreData,
  selectDocumentsOnlyWhenInRoom,
  selectRoomToken,
  selectHubConnectionId,
  selectIsHome,
  selectCountryCode,
  selectCaseDetails,
  selectIdentityId,
} from 'common/selectors';
import { setHubConnectionId } from 'features/auth/redux/setHubConnectionId';
import { fetchCaseDetails } from 'features/case/redux/actions';
import { fetchTheatreData } from 'features/viewing/redux/fetchTheatreData';
import { startPresenting } from 'features/viewing/redux/startPresenting';
import runningInDev from 'utils/runningInDev';
import { leaveAGroup } from 'features/viewing/redux/leaveAGroup';
import { setHubConnectionTransportType } from 'features/auth/redux/setHubConnectionTransportType';

// Extract seq from a realtime_line_event payload.
// Returns null if seq is not present (server hasn't added it yet).
const getSeq = (payload) => {
  if (Array.isArray(payload)) return payload[0]?.seq ?? null;
  if (payload && typeof payload === 'object') return payload.seq ?? null;
  return null;
};

const BACKLOG_DRAIN_MIN_MS = 30;
const BACKLOG_DRAIN_MAX_MS = 500;
const BACKLOG_DRAIN_FALLBACK_MS = 100;

// Extract the observedAtUtc timestamp from a message payload.
const getObservedAt = (payload) => {
  const entry = Array.isArray(payload) ? payload[0] : payload;
  const raw = entry?.observedAtUtc ?? entry?.ObservedAtUtc ?? null;
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
};

class LineEventBuffer {
  constructor(dispatch) {
    this._dispatch = dispatch;
    this._pending = new Map(); // seq → message
    this._nextSeq = null;
    this._gapTimer = null;
    this._drainTimer = null;
    this._lastAppliedAt = null; // observedAtUtc of the last dispatched message
  }

  push(msg) {
    const payload = msg.payload ?? msg.Payload;
    const seq = getSeq(payload);

    // No seq — apply immediately
    if (seq === null || seq === undefined) {
      this._apply(msg);
      return;
    }

    if (this._nextSeq === null) {
      this._nextSeq = seq;
    }

    if (seq === this._nextSeq) {
      this._flush(msg);
    } else if (seq > this._nextSeq) {
      this._pending.set(seq, msg);
      this._startGapTimer();
    }
    // seq < nextSeq → stale duplicate, discard
  }

  reset() {
    clearTimeout(this._gapTimer);
    clearTimeout(this._drainTimer);
    this._gapTimer = null;
    this._drainTimer = null;
    this._pending.clear();
    this._nextSeq = null;
    this._lastAppliedAt = null;
  }

  _apply(msg) {
    const payload = msg.payload ?? msg.Payload;
    this._lastAppliedAt = getObservedAt(payload);
    this._dispatch({ type: CASE_REALTIME_TRANSCRIPT_MESSAGE_RECEIVED, data: msg });
  }

  _flush(msg) {
    clearTimeout(this._gapTimer);
    this._gapTimer = null;
    // Apply the expected message immediately
    this._apply(msg);
    this._nextSeq += 1;
    // Drain any backlog using real timestamp gaps so replay matches original cadence
    this._drainBacklog();
  }

  _drainBacklog() {
    if (!this._pending.has(this._nextSeq)) return;
    if (this._drainTimer) return;

    const nextMsg = this._pending.get(this._nextSeq);
    const nextPayload = nextMsg.payload ?? nextMsg.Payload;
    const nextObservedAt = getObservedAt(nextPayload);

    // Compute delay: difference between this message's timestamp and the last applied.
    // Fall back to fixed interval if timestamps are unavailable.
    let delay = BACKLOG_DRAIN_FALLBACK_MS;
    if (nextObservedAt !== null && this._lastAppliedAt !== null) {
      const diff = nextObservedAt - this._lastAppliedAt;
      delay = Math.min(Math.max(diff, BACKLOG_DRAIN_MIN_MS), BACKLOG_DRAIN_MAX_MS);
    }

    this._drainTimer = setTimeout(() => {
      this._drainTimer = null;
      if (!this._pending.has(this._nextSeq)) return;
      const msg = this._pending.get(this._nextSeq);
      this._pending.delete(this._nextSeq);
      this._apply(msg);
      this._nextSeq += 1;
      this._drainBacklog();
    }, delay);
  }

  _startGapTimer() {
    if (this._gapTimer) return;
    this._gapTimer = setTimeout(() => {
      this._gapTimer = null;
      if (!this._pending.size) return;
      const lowestSeq = Math.min(...this._pending.keys());
      Logger.WARN(`RealTime: seq gap detected, skipping to seq ${lowestSeq}`);
      this._nextSeq = lowestSeq;
      const msg = this._pending.get(lowestSeq);
      this._pending.delete(lowestSeq);
      this._flush(msg);
    }, 1500);
  }
}

const signalRMiddleware = ({ getState, dispatch }) => {
  let connectionInProgress = false;
  const lineEventBuffers = new Map(); // sessionKey → LineEventBuffer

  return (next) => (action) => {
    //needed to make cases load
    const nextAction = next(action);

    const state = getState();
    const hubConnectionId = selectHubConnectionId(state);
    const countryCode = selectCountryCode(state);
    const caseDetails = selectCaseDetails(state);
    // register signalR after the user logged in
    if (
      [
        HOME_FETCH_CASES_SUCCESS,
        HOME_FETCH_CASES_SUCCESS_DUMMY,
        CASE_FETCH_CASE_DETAILS_SUCCESS,
      ].includes(action.type)
    ) {
      const caseId = selectCurrentCaseId(state);

      if (typeof caseId === 'undefined') {
        Logger.ERROR("User has no cases so we can't connect him to SignalR or fetch details");

        const isHome = selectIsHome(state);
        if (!isHome)
          dispatch({
            type: CASE_FETCH_CASE_DETAILS_FAILURE,
            feedback: {
              error: 'There is no such case.',
            },
          });

        return nextAction;
      }
      if (!caseDetails) dispatch(fetchCaseDetails());

      if (!hubConnectionId && !connectionInProgress && caseDetails) {
        connectionInProgress = true;
        const startSignalRConnection = (connection) =>
          connection
            .start()
            .then(() => {
              connectionInProgress = false;
              dispatch(setHubConnectionId(connection.connectionId));
              Logger.INFO(`SignalR Connected with connectionId: ${connection.connectionId}`);
              const connectionTypeObj = Object.keys(connection.connection.transport);
              let connectionType = null;
              if (connectionTypeObj.includes('_webSocket')) connectionType = 'WebSockets';
              else if (connectionTypeObj.includes('_pollAbort')) connectionType = 'LongPolling';
              else if (connectionTypeObj.includes('_eventSource'))
                connectionType = 'ServerSentEvents';
              else connectionType = 'unknown';
              if (connectionType === 'unknown') {
                Logger.INFO(
                  'SignalR is using unknown transport type, connection object:',
                  connection,
                );
              }
              dispatch(setHubConnectionTransportType(connectionType));
              Logger.INFO(`SignalR is using transport type: ${connectionType}`);
            })
            .catch((err) => {
              connectionInProgress = false;
              Logger.ERROR('SignalR Connection Error: ', err);
              dispatch({
                type: AUTH_SET_HUB_CONNECTION_ID_FAILURE,
                feedback: {
                  message: 'feedback.signalRError',
                  error: 'SignalR Connection Error: ' + err,
                },
              });
            });
        const urlKey = `REACT_APP_API_${countryCode}`;
        const url = process.env[urlKey];
        const protocol = new JsonHubProtocol();

        // allow client and server to negotiate the best protocol available
        //const transport = HttpTransportType.None;
        const transport = HttpTransportType.WebSockets | HttpTransportType.LongPolling;

        const options = {
          accessTokenFactory: getAccessToken,
          transport,
        };

        const hubConnection = new HubConnectionBuilder()
          .withUrl(`${url}/cases/${caseId}`, options)
          .withAutomaticReconnect([
            0, 2000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
            5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
            5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
            5000, 5000, 5000, 5000, 5000, 5000, 5000,
          ])
          .configureLogging(runningInDev ? LogLevel.Debug : LogLevel.Information)
          .withHubProtocol(protocol)
          .build();

        hubConnection.onreconnected((connectionId) => {
          lineEventBuffers.forEach((buf) => buf.reset());
          lineEventBuffers.clear();
          const state = getState();
          const oldHubConnectionId = selectHubConnectionId(state);
          const query = selectQuery(state);
          const shouldRestartPresent = !!query.present;
          const url = selectMatchedUrl(state);
          const isPublicRoom = url.includes('public-hearing-room');
          const isPrivateRoom = url.includes('private-team-room');
          const shouldRefetchData = isPublicRoom || isPrivateRoom;
          if (oldHubConnectionId) {
            if (shouldRestartPresent) dispatch(leaveAGroup('present'));
            if (shouldRefetchData) dispatch(leaveAGroup('receive'));
          }
          dispatch(setHubConnectionId(connectionId));
          if (shouldRefetchData) {
            const state = getState();
            const hearingRoomMode = isPublicRoom ? 'public' : 'private';
            const documentsOnlyWhenInRoom = selectDocumentsOnlyWhenInRoom(state);
            const isJoinedToRoom = selectRoomToken(state, hearingRoomMode);
            if (!documentsOnlyWhenInRoom || isJoinedToRoom) {
              dispatch(fetchTheatreData({ hearingRoomMode }));
            }
          }
          if (shouldRestartPresent) {
            const state = getState();
            const query = selectQuery(state);
            const theatreData = selectTheatreData(state, query.present);
            theatreData.messages?.forEach((message) => {
              dispatch(
                startPresenting({
                  message: message,
                  hearingRoomMode: query.present,
                }),
              );
            });
          }
        });

        // event handlers, you can use these to dispatch actions to update your Redux store
        hubConnection.on('ai-answer', (res) => {
          dispatch({ type: 'CASE_FETCH_SIGNALR_ANSWER_SUCCESS', data: res });
        });
        hubConnection.on('notification', (res) => {
          dispatch({
            type: 'CASE_FETCH_SIGNALR_NOTIFICATION_SUCCESS',
            data: { notification: res },
            feedback: null,
          });
          if (!res.transient)
            dispatch({
              type: 'CASE_FETCH_SIGNALR_NOTIFICATION_FEEDBACK',
              feedback: {
                error: res.status === 'failed',
                message: res.message || 'feedback.mergeDocumentsSuccess',
              },
            });
        });

        hubConnection.on('present', (res) => {
          dispatch({ type: 'VIEWING_FETCH_THEATRE_DATA_SUCCESS', data: res });
        });

        hubConnection.on('hearing-room-session-changed', (res) =>
          dispatch({
            type: 'CONFERENCE_FETCH_HEARING_ROOM_SESSION_SUCCESS',
            data: {
              hearingRoomSessionStarted: res.sessionState === 'started',
              hearingRoomMessage: res.message,
            },
          }),
        );
        hubConnection.on('realtimeTranscripts', (res) => {
          const messageType = res?.messageType ?? res?.MessageType;
          if (messageType !== 'realtime_line_event') {
            // Ignore note notifications created by the current user — the local
            // state is already up to date from the action that created the note.
            if (messageType === 'rt_note_notification') {
              const identityId = selectIdentityId(getState());
              const payload = res?.payload ?? res?.Payload;
              const createdByUserId = payload?.createdByUserId ?? payload?.CreatedByUserId;
              if (identityId && createdByUserId === identityId) return;
            }
            // Session notifications, notes — no ordering needed, apply immediately
            dispatch({ type: CASE_REALTIME_TRANSCRIPT_MESSAGE_RECEIVED, data: res });
            return;
          }
          const metadata = res?.metadata ?? res?.Metadata;
          const sessionKey = metadata?.sessionKey ?? metadata?.SessionKey ?? 'default';
          if (!lineEventBuffers.has(sessionKey)) {
            lineEventBuffers.set(sessionKey, new LineEventBuffer(dispatch));
          }
          lineEventBuffers.get(sessionKey).push(res);
        });
        
        hubConnection.on('iManageJobCompleted', (res) => {
          dispatch({ type: 'CASE_IMANAGE_JOB_COMPLETED', data: res });
        });
        hubConnection.on('iManageJobStarted', (res) => {
          dispatch({ type: 'CASE_IMANAGE_JOB_STARTED', data: res });
        });
        hubConnection.on('iManageDocumentStatus', (res) => {
          dispatch({ type: 'CASE_IMANAGE_DOCUMENT_STATUS', data: res });
        });
        hubConnection.on('system', (res) => Logger.INFO('****** SYSTEM NOTIFICATION ******', res));

        hubConnection.onclose(() => Logger.INFO('SignalR disconnected'));

        startSignalRConnection(hubConnection);
      }
    }
    return nextAction;
  };
};

export default signalRMiddleware;
